import { List } from "immutable";
import { RootState } from "common/reducers";
import { image as imageSelector } from "common/selectors/image.selectors";
import { document as documentSelector } from "common/selectors/document.selectors";
import { fetchFileFromUrl } from "common/helpers/File";

/**
 * A resolved file paired with the id of the source record it came from. `sourceId` lets a caller
 * recover per-file metadata (e.g. the source attachment's `display` setting) that the raw `File`
 * doesn't carry — necessary because resolveFiles drops any attachment it can't read, so the result
 * can't be positionally aligned back to the input `ids`.
 */
export interface ResolvedFile {
  file: File;
  sourceId: number;
}

/**
 * Resolves Files for a set of image/document ids: a staged (negative-id) record carries its
 * `file_queue` File directly; a saved record's blob is downloaded from its file url. Used both to
 * upload an estimate's custom-item files onto a template (save) and to download a template's files for
 * re-staging onto a new line item (apply), and by the estimate-duplicate cascade to re-upload a source
 * line item's owned attachments onto its clone. Each result carries its `sourceId` so the caller can
 * read source-record metadata (the duplicate cascade uses it to preserve each attachment's `display`).
 *
 * Lives here (rather than on an async-actions module) so both the estimate-template and estimate-editor
 * async-action modules can import it without forming a static import cycle between them.
 */
export const resolveFiles = async (
  state: RootState,
  ids: List<number>,
  kind: "image" | "document",
): Promise<ResolvedFile[]> => {
  // Fire all downloads concurrently and await them together — a staged file resolves immediately, and
  // saved files' blob downloads have no interdependency. `.map` preserves index order, so the returned
  // files stay in the same order as `ids`.
  const resolved = await Promise.all(
    ids.toArray().map(async (id): Promise<ResolvedFile | null> => {
      const record =
        kind === "image" ? imageSelector(state, { imageId: id }) : documentSelector(state, { documentId: id });
      if (!record) return null;
      if (record.file_queue instanceof File) return { file: record.file_queue, sourceId: id };
      const url = kind === "image" ? record.file?.get("url") : record.url || record.file?.get("url");
      if (!url) return null;
      try {
        if (kind === "document") {
          // Documents live behind an auth-gated rsf-api URL (api/v2/documents/:id/url); a bare fetch
          // sends no Authorization header and 401s. Route through the authenticated `fetcher`, which
          // injects the bearer token.
          const file = await fetchFileFromUrl(url, record.name || `file-${id}`);
          return { file, sourceId: id };
        }
        // Images resolve from a direct/presigned storage URL. These must use a bare fetch: adding an
        // Authorization header makes S3 reject a presigned request ("only one auth mechanism allowed").
        const response = await fetch(url);
        // fetch() doesn't reject on 404/403 — guard on response.ok so an error page is never
        // re-uploaded as a corrupted attachment (parity with exportFileToS3).
        if (!response.ok) return null;
        const blob = await response.blob();
        return { file: new File([blob], record.name || `file-${id}`, { type: blob.type }), sourceId: id };
      } catch {
        // A file that can't be downloaded is skipped rather than failing the whole save/apply.
        return null;
      }
    }),
  );
  return resolved.filter((resolvedFile): resolvedFile is ResolvedFile => resolvedFile !== null);
};
